/**
 * MaestroChatPanel pipeline data + helpers (pure data, no React).
 *
 * The Create Media Plan accordion renders one row per step of the
 * `wf_media-planning` workflow as configured in the Workflow Manager. Rows —
 * their labels, their order, and which row a streamed event lands in — come
 * entirely from the fetched steps and the `step_id` the llm-service puts on
 * every orchestrator event (ORCH-286 §5.9, P6). A step added in the Workflow
 * Manager therefore appears as a row with no frontend change.
 *
 * The engine's own `phase` keys (brief_intake, research_audience, …) survive on
 * those events — they label ORCH-242 prompt-cache telemetry and identify the
 * individual agent inside a step — but they no longer decide which row anything
 * renders in. `PHASE_LABELS` / `phaseLabel` remain only for chat mode, where
 * there is no workflow and sections are keyed by engine phase.
 */

export interface PhaseSection {
  /** Stable section key: the workflow `step_id` in plan mode, the engine phase in chat mode. */
  key: string;
  cot: string;
  toolCallIds: string[];
  outputText: string;
  /** After intake completes (continue_past_brief=false): show this instead of raw CoT when reopening. */
  summaryMarkdown?: string;
  completed: boolean;
  /** Future pipeline steps not yet reached — accordion stays collapsed & disabled. */
  locked: boolean;
}

/** Workflow whose steps populate the Create Media Plan accordion (business lifecycle steps). */
export const PLAN_WORKFLOW_ID = 'wf_media-planning';

/**
 * Display labels for orchestrator (engine) phases — chat mode only. Plan-mode
 * accordion rows use the workflow step's own label.
 */
export const PHASE_LABELS: Record<string, string> = {
  brief_intake: 'Brief Intake',
  brief_review: 'Brief Review',
  research_sprint: 'Research Sprint',
  research_review: 'Research Review',
  strategy_dev: 'Strategy Development',
  strategy_review: 'Strategy Review',
  plan_generation: 'Plan Generation',
  plan_review: 'Plan Review',
  research_audience: 'Audience Research',
  research_market: 'Market Intelligence',
  research_risk: 'Risk & Assumptions Auditor',
  react: 'Analysis',
  complete: 'Complete',
};

/** Minimal step shape from Workflow Manager needed to drive the Maestro accordion. */
export interface PlanStepShape {
  step_id: string;
  label: string;
  sequence_order: number;
  /** Engine phase keys this business step covers (derived server-side from step_key). */
  engine_phases?: string[];
  /** Engine capability this step runs; null/absent = business lifecycle step with no AI behaviour. */
  step_type?: string | null;
}

/** The step identity llm-service puts on orchestrator SSE events (ORCH-286 P6). */
export interface StepEventRef {
  step_id?: string;
  phase?: string;
}

/** Build the initial plan pipeline from fetched Workflow Manager steps. */
export function buildPlanPipelineFromSteps(steps: PlanStepShape[]): PhaseSection[] {
  return steps.map((step) => ({
    key: step.step_id,
    cot: '',
    toolCallIds: [],
    outputText: '',
    summaryMarkdown: undefined,
    completed: false,
    locked: true,
  }));
}

/**
 * Unlock the first pipeline row so it renders active as soon as an orchestrator
 * run actually starts (brief attached, stream kicked off) — rather than staying
 * locked until the backend's first phase SSE event arrives. Without this, the
 * Briefing row stays visibly locked for the entire time Maestro is processing
 * the brief, before any phase event has streamed back (ORCH-273).
 */
export function unlockFirstStep(sections: PhaseSection[]): PhaseSection[] {
  if (!sections.length) return sections;
  return sections.map((s, idx) => (idx === 0 ? { ...s, locked: false } : s));
}

/**
 * Resolve the accordion section an event's content lands in.
 *
 * `step_id` is authoritative — every phase of a step, including a
 * `parallel_produce` step's sub-agents, carries the parent step's id, so they
 * all append into one row. `engine_phases` is the fallback for an llm-service
 * that predates P6; failing both (chat mode), the engine phase is its own
 * section key.
 */
export function sectionKeyForEvent(event: StepEventRef, steps: PlanStepShape[]): string {
  const stepId = (event.step_id || '').trim();
  if (stepId && steps.some((s) => s.step_id === stepId)) return stepId;
  const phase = event.phase || '';
  const wired = steps.find((s) => (s.engine_phases ?? []).includes(phase));
  return wired ? wired.step_id : phase;
}

/** Index of the workflow row a section key names, or -1 when it isn't a row (chat-mode section). */
export function stepIndexForKey(key: string, steps: PlanStepShape[]): number {
  if (!key) return -1;
  return steps.findIndex((s) => s.step_id === key);
}

/**
 * The step that owns brief intake — the `form_intake` capability's row. The
 * intake and brief-review handoffs land here whatever the admin has named the
 * step. Falls back to the first row when `step_type` isn't available.
 */
export function formIntakeStep(steps: PlanStepShape[]): PlanStepShape | undefined {
  return steps.find((s) => s.step_type === 'form_intake') ?? steps[0];
}

/**
 * The first row with no engine behaviour — the handover point from the AI
 * pipeline to the manual lifecycle ('Plan Approval' in the seeded workflow).
 * Derived from the steps rather than matched by label, so renaming or
 * reordering rows in the Workflow Manager needs no frontend change.
 */
export function firstNonEngineStep(steps: PlanStepShape[]): PlanStepShape | undefined {
  return steps.find((s) => !(s.engine_phases ?? []).length);
}

/**
 * Sections state after the AI pipeline finalizes (plan approved): rows before
 * the first non-engine row complete, that row becomes the active one, and later
 * lifecycle rows stay locked. Sections with no such row (chat mode) simply
 * complete everything, as before.
 */
export function applyPipelineComplete(
  sections: PhaseSection[],
  steps: PlanStepShape[]
): PhaseSection[] {
  const handoverStep = firstNonEngineStep(steps);
  const handoverIdx = handoverStep ? sections.findIndex((s) => s.key === handoverStep.step_id) : -1;
  if (handoverIdx < 0) {
    return sections.map((s) => ({ ...s, completed: true, locked: false }));
  }
  return sections.map((s, idx) => ({
    ...s,
    completed: idx < handoverIdx,
    locked: idx > handoverIdx,
  }));
}

/** Phases that must not become accordion rows (connector / lifecycle noise from SSE). */
export const SKIP_PHASES = new Set([
  '',
  'started',
  'resume_started',
  'run_started',
  'run_completed',
  'complete',
  /** Human approval node — not a workflow row; do not add an accordion or replace pipeline state. */
  'awaiting_approval',
]);

/** Chat-mode section heading for an engine phase. */
export function phaseLabel(phase: string): string {
  return PHASE_LABELS[phase] || phase.replace(/_/g, ' ');
}

/**
 * The Maestro progress line's wording (ORCH-406), keyed by what the run reports.
 * Two sources feed it: the llm-service's `progress` events name extraction
 * stages (`brief_*`), and `tool_called` events name the tool — the stage keys
 * are a contract with `orchestrator._emit_progress`, so a renamed key needs this
 * map updated in the same change. The backend sends keys, never display text.
 */
export const PROGRESS_LABELS: Record<string, string> = {
  brief_download: 'Reading the brief',
  brief_convert: 'Converting the document',
  brief_extract: 'Parsing the document',
  brief_analyse: 'Reading the brief contents',
  // Tool-driven stages, keyed by tool_name.
  mdm_search_values: 'Mapping master data values',
  mdm_resolve_field: 'Mapping master data values',
  mdm_validate_value: 'Checking master data values',
  metadata_get_model_view: "Reading the plan's fields",
  metadata_get_form_schema: "Reading the plan's fields",
  apply_create_plan_fields: 'Filling in the plan',
};

/** What the progress line says from the moment a run starts until the first stage reports. */
export const PROGRESS_START_LABEL = 'Reading the brief';

/**
 * Progress-line text for a stage key or tool name; `null` when it has none, so
 * the caller keeps the previous line rather than showing a raw key.
 */
export function progressLabel(key: string | null | undefined): string | null {
  return key && Object.prototype.hasOwnProperty.call(PROGRESS_LABELS, key)
    ? PROGRESS_LABELS[key]
    : null;
}
